import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Square, Loader2 } from "lucide-react";
import { CTAButton } from "./primitives";

const API = process.env.REACT_APP_BACKEND_URL;
const DOMAINS = [
  "Opening", "World View", "Relationships", "Identity",
  "Authority & Power", "Emotions", "Growth", "Closing",
];

const Bubble = ({ role, text }) => {
  const ai = role === "assistant";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={`flex ${ai ? "justify-start" : "justify-end"}`}
    >
      <div
        className={`max-w-[85%] sm:max-w-[75%] px-5 py-4 rounded-2xl font-sans text-base leading-relaxed whitespace-pre-wrap ${
          ai
            ? "bg-transparent border border-[#C9A84C]/25 text-[#F5F0E8] rounded-tl-sm"
            : "bg-[#F0EAE0] text-[#1A2535] rounded-tr-sm"
        }`}
        data-testid={`bubble-${role}`}
      >
        {text}
      </div>
    </motion.div>
  );
};

const SpiralAssessmentPage = () => {
  const [stage, setStage] = useState("entry"); // entry | chat | done
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [domainIndex, setDomainIndex] = useState(0);
  const [notice, setNotice] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  // auto-save every 30s
  useEffect(() => {
    if (!sessionId) return;
    const id = setInterval(() => {
      try {
        localStorage.setItem(
          "spiral_" + sessionId,
          JSON.stringify({ messages, domainIndex, name, email })
        );
      } catch (e) {}
    }, 30000);
    return () => clearInterval(id);
  }, [sessionId, messages, domainIndex, name, email]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const begin = async () => {
    if (!name.trim() || !emailValid || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/spiral/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });
      const d = await r.json();
      setSessionId(d.session_id);
      setMessages([{ role: "assistant", text: d.message }]);
      setDomainIndex(d.domain_index || 0);
      setStage("chat");
    } catch (e) {
      setNotice("Something went wrong starting the assessment. Please refresh and try again.");
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy || !sessionId) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/spiral/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const d = await r.json();
      setMessages((m) => [...m, { role: "assistant", text: d.message }]);
      setDomainIndex(d.domain_index ?? domainIndex);
      if (d.is_final) await finish();
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "The guide is taking a moment. Please send that again." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const pollStatus = (sid, attempts = 0) => {
    if (attempts > 30) return; // ~5 min max
    setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/spiral/status/${sid}`);
        const d = await r.json();
        if (d.failed && d.notice) {
          setNotice(d.notice);
          return;
        }
        if (d.report_ready && d.emailed) return; // done
        pollStatus(sid, attempts + 1);
      } catch (e) {
        pollStatus(sid, attempts + 1);
      }
    }, 10000);
  };

  const finish = async () => {
    try {
      const r = await fetch(`${API}/api/spiral/finish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const d = await r.json();
      setMessages((m) => [...m, { role: "assistant", text: d.message }]);
      setStage("done");
      pollStatus(sessionId);
    } catch (e) {
      setNotice(
        "Your report is taking a moment to generate. Aaron will send it to you manually before your session — apologies for the delay."
      );
      setStage("done");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const pickMime = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
    for (const c of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  };

  const transcribeBlob = async (blob, mime) => {
    setTranscribing(true);
    try {
      const ext = mime.includes("mp4") ? "mp4" : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `answer.${ext}`);
      const r = await fetch(`${API}/api/spiral/transcribe`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("transcribe failed");
      const d = await r.json();
      const text = (d.text || "").trim();
      if (text) {
        setInput((prev) => (prev.trim() ? prev.trim() + " " + text : text));
        setNotice("");
      } else {
        setNotice("I didn't quite catch that — please try again or type your answer.");
      }
    } catch (e) {
      setNotice("Sorry, I couldn't transcribe that. Please try again or type your answer.");
    } finally {
      setTranscribing(false);
    }
  };

  const startRecording = async () => {
    if (busy || transcribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = mr.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size > 0) await transcribeBlob(blob, type);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setNotice("");
    } catch (e) {
      setNotice("I couldn't access your microphone. Please allow mic access in your browser, or type your answer.");
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
      setRecording(false);
    }
  };

  const toggleMic = () => (recording ? stopRecording() : startRecording());

  return (
    <div className="min-h-screen bg-[#0D1B2A] font-sans antialiased text-[#F5F0E8] selection:bg-[#C9A84C]/30">
      {/* progress */}
      {stage !== "entry" && (
        <div className="fixed top-0 left-0 w-full z-40 bg-[#0D1B2A]/90 backdrop-blur-md border-b border-[#C9A84C]/15 px-4 sm:px-6 py-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="font-serif text-lg tracking-[0.3em] text-[#C9A84C]">AC</span>
              <span className="font-sans text-[0.6rem] sm:text-xs tracking-[0.2em] uppercase text-[#C9A84C]">
                {DOMAINS[domainIndex]} · {domainIndex + 1}/8
              </span>
            </div>
            <div className="h-1 w-full bg-[#F5F0E8]/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#C9A84C] to-[#D4691E] transition-all duration-700"
                style={{ width: `${((domainIndex + 1) / 8) * 100}%` }}
                data-testid="progress-bar"
              />
            </div>
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        {stage === "entry" ? (
          <motion.div
            key="entry"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex items-center justify-center px-6 py-24"
          >
            <div className="max-w-2xl mx-auto text-center">
              <h1 className="font-serif font-medium uppercase text-[#C9A84C] text-4xl sm:text-5xl md:text-6xl leading-tight">
                Your Spiral Analysis
              </h1>
              <div className="mt-10 space-y-5 font-sans font-light text-[#F5F0E8]/85 text-base sm:text-lg leading-relaxed text-left">
                <p className="text-center">Welcome. Before we begin, a few things worth knowing.</p>
                <p>
                  This conversation will take approximately 25–35 minutes. There are no right or
                  wrong answers — the more honest you are, the more useful this will be.
                </p>
                <p>
                  An AI will guide you through a series of questions across eight areas of your
                  life. At the end, a personalised report will be generated and sent to both you and
                  Aaron before your session together.
                </p>
                <p>
                  A heads up: AI can get a lot right — and it can also make assumptions that miss the
                  mark. That's not a flaw, it's part of the process. Your session with Aaron exists
                  to sift through what resonated, challenge what didn't, and build something accurate
                  and useful from what actually came up. Bring your reactions to the report into the
                  call.
                </p>
                <p className="font-serif italic text-xl text-[#C9A84C] text-center !mt-8">
                  Find somewhere quiet. Take your time. This is for you.
                </p>
              </div>

              <div className="mt-12 text-left">
                <label className="block font-sans text-[0.7rem] tracking-[0.2em] uppercase text-[#C9A84C] mb-4 text-center">
                  First, what's your name and email so we can send your report?
                </label>
                <div className="space-y-4 max-w-md mx-auto">
                  <input
                    data-testid="entry-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="First name"
                    className="w-full bg-transparent border border-[#C9A84C]/40 focus:border-[#C9A84C] rounded-lg px-5 py-3 text-[#F5F0E8] placeholder-[#F5F0E8]/40 outline-none transition-colors"
                  />
                  <input
                    data-testid="entry-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    className="w-full bg-transparent border border-[#C9A84C]/40 focus:border-[#C9A84C] rounded-lg px-5 py-3 text-[#F5F0E8] placeholder-[#F5F0E8]/40 outline-none transition-colors"
                  />
                </div>
                <div className="mt-8 flex justify-center">
                  <button
                    onClick={begin}
                    disabled={!name.trim() || !emailValid || busy}
                    data-testid="begin-btn"
                    className="inline-flex items-center justify-center border border-[#C9A84C] text-[#C9A84C] px-9 sm:px-11 py-4 uppercase tracking-[0.18em] text-xs sm:text-sm font-sans font-medium transition-all duration-500 hover:bg-[#C9A84C]/[0.06] hover:shadow-[0_0_28px_rgba(201,168,76,0.28)] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {busy ? "Starting…" : "Begin Your Assessment"}
                  </button>
                </div>
                {notice && <p className="mt-6 text-center text-[#D4691E] text-sm">{notice}</p>}
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="chat"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-40"
          >
            <div ref={scrollRef} className="space-y-6">
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} text={m.text} />
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="px-5 py-4 rounded-2xl border border-[#C9A84C]/25 text-[#C9A84C]/70 text-sm">
                    <span className="inline-flex gap-1">
                      <span className="animate-pulse">•</span>
                      <span className="animate-pulse" style={{ animationDelay: "0.2s" }}>•</span>
                      <span className="animate-pulse" style={{ animationDelay: "0.4s" }}>•</span>
                    </span>
                  </div>
                </div>
              )}
            </div>

            {notice && (
              <p className="mt-8 text-center text-[#D4691E] text-sm max-w-xl mx-auto">{notice}</p>
            )}

            {stage === "chat" && (
              <div className="fixed bottom-0 left-0 w-full bg-[#0D1B2A]/95 backdrop-blur-md border-t border-[#C9A84C]/15 px-4 sm:px-6 py-4">
                <div className="max-w-3xl mx-auto">
                  {(recording || transcribing) && (
                    <p
                      data-testid="voice-status"
                      className="mb-2 text-center text-xs tracking-[0.15em] uppercase text-[#C9A84C]/80"
                    >
                      {recording ? "Listening… tap the mic to stop" : "Transcribing your answer…"}
                    </p>
                  )}
                  <div className="flex items-end gap-3">
                    <textarea
                      data-testid="chat-input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKey}
                      rows={1}
                      placeholder="Type your answer, or tap the mic to speak…"
                      disabled={busy}
                      className="flex-1 resize-none bg-transparent border border-[#C9A84C]/40 focus:border-[#C9A84C] rounded-xl px-5 py-3 text-[#F5F0E8] placeholder-[#F5F0E8]/40 outline-none transition-colors max-h-40"
                    />
                    <button
                      onClick={toggleMic}
                      disabled={busy || transcribing}
                      data-testid="chat-mic"
                      aria-label={recording ? "Stop recording" : "Record your answer"}
                      title={recording ? "Stop recording" : "Speak your answer"}
                      className={`shrink-0 w-12 h-12 flex items-center justify-center rounded-xl border transition-all disabled:opacity-40 ${
                        recording
                          ? "border-[#D4691E] text-[#D4691E] bg-[#D4691E]/10 animate-pulse"
                          : "border-[#C9A84C]/60 text-[#C9A84C] hover:bg-[#C9A84C]/[0.06]"
                      }`}
                    >
                      {transcribing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : recording ? (
                        <Square className="w-4 h-4 fill-current" />
                      ) : (
                        <Mic className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={send}
                      disabled={busy || transcribing || !input.trim()}
                      data-testid="chat-send"
                      className="shrink-0 border border-[#C9A84C] text-[#C9A84C] px-6 py-3 rounded-xl uppercase tracking-[0.15em] text-xs font-medium hover:bg-[#C9A84C]/[0.06] transition-all disabled:opacity-40"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            )}

            {stage === "done" && (
              <div className="mt-14 text-center">
                <div className="flex items-center justify-center gap-3 mb-6" aria-hidden="true">
                  <span className="w-10 h-px bg-[#C9A84C]" />
                  <span className="w-10 h-px bg-[#C9A84C]" />
                </div>
                <CTAButton testId="spiral-home-cta" href="https://aaronchristos.com">
                  Return to aaronchristos.com
                </CTAButton>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SpiralAssessmentPage;
